import React, {createContext, useReducer, useContext, useMemo, useRef, useEffect, ReactNode, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useScrollAtTop} from '_customHooks'

interface HeaderState {
    /** Reference for header DOM element. */
    headerRef?: React.RefObject<HTMLElement>

    /** Indicates if user has scrolled to the top of the page. */
    isAtTop: boolean

    /** Indicates if search section is opened or not. */
    isSearchSectionEnabled: boolean

    /** Indicates if the mobile sidebar is opened or not. */
    isSidebarOpen: boolean

    /** Indicates if transparent mode is enabled or not. */
    isTransparentMode: boolean

    /** When defined, overrides the computed transparent mode value. */
    transparentModeOverride?: boolean

    /** Indicates if the banner can be rendered or not. */
    showBanner: boolean

    /** Indicates if a menu item is being hovered. */
    isMenuItemHovered: boolean

    /** Indicates if the user is currently in the homepage. */
    isHomepage: boolean
}

const initialState: HeaderState = {
    headerRef: undefined,
    isAtTop: false,
    isSearchSectionEnabled: false,
    isSidebarOpen: false,
    isTransparentMode: false,
    transparentModeOverride: undefined,
    showBanner: true,
    isMenuItemHovered: false,
    isHomepage: false,
}

/**
 * HeaderDispatch interface defines the actions and dispatch functions available
 * for managing the state and behavior of the header component.
 */
interface HeaderDispatch {
    /**
     * Dispatch function to send actions to the header reducer.
     *
     * @param {HeaderAction} action - The action to be dispatched, which modifies the header state.
     * @param {HeaderActionType} action.type - Type of the action to be dispatched.
     * @param {HeaderActionPayload} action.payload - Payload of the action to be dispatched.
     */
    dispatch: React.Dispatch<HeaderAction>

    /**
     * Overrides the computed transparent mode with the given value.
     * Pass `undefined` to remove the override and revert to computed behaviour.
     *
     * @param {boolean | undefined} value - The desired transparent mode value.
     */
    setTransparentMode: (value: boolean | undefined) => void
}

enum HeaderActionType {
    TOGGLE_SEARCH_SECTION = 'TOGGLE_SEARCH_SECTION',
    TOGGLE_SIDEBAR = 'TOGGLE_SIDEBAR',
    HOVER_MENU_ITEM = 'MENU_ITEM_HOVERED',
    SET_TRANSPARENT_MODE = 'SET_TRANSPARENT_MODE',
}

type HeaderActionPayload = boolean | undefined
type HeaderAction = {type: HeaderActionType; payload?: HeaderActionPayload}

const StateContext = createContext<HeaderState | undefined>(undefined)
const DispatchContext = createContext<HeaderDispatch | undefined>(undefined)

const reducer = (state: HeaderState, action: HeaderAction): HeaderState => {
    switch (action.type) {
        case HeaderActionType.TOGGLE_SEARCH_SECTION:
            return {...state, isSearchSectionEnabled: !state.isSearchSectionEnabled}
        case HeaderActionType.TOGGLE_SIDEBAR:
            return {...state, isSidebarOpen: !state.isSidebarOpen}
        case HeaderActionType.HOVER_MENU_ITEM:
            return {...state, isMenuItemHovered: action.payload!}
        case HeaderActionType.SET_TRANSPARENT_MODE:
            return {...state, transparentModeOverride: action.payload}
        default:
            return state
    }
}

/**
 * @name HeaderProvider
 *
 * @provider
 *
 * @description
 * Provides header states and actions about header business logic.
 * The internal separation in 2 specific providers helps with performance optimization.
 *
 * @param {ReactNode} children
 *
 * @returns {JSX.Element} - Wrapped children.
 */
const HeaderProvider = ({children}: {children: ReactNode}): JSX.Element => {
    const location = useLocation()
    const {locale} = useMultiSite()
    const isAtTop = useScrollAtTop()
    const headerRef = useRef<HTMLElement>(null)
    const [state, dispatch] = useReducer(reducer, {...initialState, isAtTop, headerRef})

    const isHomepage = new RegExp(`^/${locale.alias}/?$`).test(location.pathname)
    const isWishlistPage = new RegExp(`^/${locale.alias}/wishlist/?$`).test(location.pathname)
    const isErrorPage = typeof window !== 'undefined' && document?.querySelector('.error-page') !== null
    // TODO: Identify other pages that could have this transparent behaviour
    const canEnableTransparentMode = isHomepage

    const isTransparentMode = useMemo(
        () =>
            state.transparentModeOverride !== undefined
                ? state.transparentModeOverride
                : canEnableTransparentMode && isAtTop && !state.isSearchSectionEnabled && !state.isMenuItemHovered,
        [state.transparentModeOverride, canEnableTransparentMode, isAtTop, state.isSearchSectionEnabled, state.isMenuItemHovered],
    )

    const showBanner = useMemo(
        () => !state.isMenuItemHovered && (isHomepage || isWishlistPage || isErrorPage),
        [state.isMenuItemHovered, isHomepage, isWishlistPage, isErrorPage],
    )

    /**
     * This function checks if the click occurred inside or outside the header element
     * and performs appropriate actions such as toggling the search section.
     *
     * @param {MouseEvent} event - The mouse event triggered by a click.
     */
    const handleBodyClick = useCallback(
        (event: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
                // Click occurred outside the header element
                if (state.isSearchSectionEnabled) {
                    dispatch({type: HeaderActionType.TOGGLE_SEARCH_SECTION})
                }
            }
        },
        [state.isSearchSectionEnabled],
    )

    /**
     * Closes the search section and sidebar on route change.
     */
    useEffect(() => {
        if (state.isSearchSectionEnabled) {
            dispatch({type: HeaderActionType.TOGGLE_SEARCH_SECTION})
        }

        if (state.isSidebarOpen) {
            dispatch({type: HeaderActionType.TOGGLE_SIDEBAR})
        }
    }, [location])

    useEffect(() => {
        document.body.addEventListener('click', handleBodyClick)

        return () => {
            document.body.removeEventListener('click', handleBodyClick)
        }
    }, [handleBodyClick])

    const setTransparentMode = useCallback(
        (value: boolean | undefined) => {
            dispatch({type: HeaderActionType.SET_TRANSPARENT_MODE, payload: value})
        },
        [dispatch],
    )

    return (
        <StateContext.Provider value={{...state, isAtTop, isTransparentMode, showBanner, isHomepage}}>
            <DispatchContext.Provider value={{dispatch, setTransparentMode}}>{children}</DispatchContext.Provider>
        </StateContext.Provider>
    )
}

/**
 * @name useHeaderState
 *
 * @hook
 *
 * @description
 * Custom hook that provides access to the header state context.
 * This hook must be used within a HeaderProvider.
 *
 * @throws {Error} If the hook is used outside of a HeaderProvider.
 *
 * @returns {HeaderState} The current state from the header context.
 */
const useHeaderState = (): HeaderState => {
    const context = useContext(StateContext)
    if (context === undefined) {
        throw new Error('useHeaderState must be used within a HeaderProvider')
    }
    return context
}

/**
 * @name useHeaderDispatch
 *
 * @hook
 *
 * @description
 * Custom hook that provides access to the header dispatch context.
 * This hook must be used within a HeaderProvider.
 *
 * @throws {Error} If the hook is used outside of a HeaderProvider.
 *
 * @returns {HeaderDispatch} The current dispatch function from the header context.
 */
const useHeaderDispatch = (): HeaderDispatch => {
    const context = useContext(DispatchContext)
    if (context === undefined) {
        throw new Error('useHeaderDispatch must be used within a HeaderProvider')
    }
    return context
}

export {HeaderProvider, useHeaderState, useHeaderDispatch, HeaderActionType}
